import { Directive, inject, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { Subject } from 'rxjs';
import { distinctUntilChanged, skip, takeUntil } from 'rxjs/operators';

import { SelectController } from '../services/select-controller.service';
import { SelectItemsService } from '../services/select-items.service';

@Directive({
  standalone: true,
  selector: 'rt-select2[withKeyNav]',
})
export class WithKeyNavDirective {
  private ngOnDestroy$ = new Subject<void>();

  private selectController = inject(SelectController);
  private selectItemsService = inject(SelectItemsService);
  private injector = inject(Injector);

  public ngOnInit() {
    toObservable(this.selectController.isOpen, { injector: this.injector })
      .pipe(distinctUntilChanged(), skip(1), takeUntil(this.ngOnDestroy$))
      .subscribe(isOpen => {
        if (!isOpen) {
          const items = this.selectItemsService.itemsToRender();
          const { selected } = this.selectController.itemSelector;
          const index = Math.max(0, items.indexOf(selected()!));

          this.selectController.navigation$.next({ type: 'item', index, visible: false });
          this.selectController.focusTrigger$.next();
        }
      });

    this.selectController.keyDown$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(event => this.handleKeyDown(event.key));
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  // eslint-disable-next-line max-lines-per-function
  private handleKeyDown(key: string) {
    const { value: selection } = this.selectController.navigation$;
    const filteredItems = this.selectItemsService.itemsToRender();
    const isDropdownOpened = this.selectController.isOpen();

    switch (key) {
      case 'ArrowDown': {
        if (!isDropdownOpened) return this.selectController.toggle(true);

        if (selection.index < filteredItems.length - 1) {
          const nextIndex = selection.index + 1;
          this.selectController.navigation$.next({ type: 'item', index: nextIndex, visible: true });
          this.selectController.scrollToIndex(nextIndex);
        }
        break;
      }

      case 'ArrowUp': {
        if (!isDropdownOpened) return this.selectController.toggle(true);

        if (selection.index > 0) {
          const nextIndex = selection.index - 1;
          this.selectController.navigation$.next({ type: 'item', index: nextIndex, visible: true });
          this.selectController.scrollToIndex(nextIndex);
        }
        break;
      }

      case 'Enter': {
        if (!isDropdownOpened) {
          return this.selectController.toggle(true);
        }

        this.selectController.select$.next(filteredItems[selection.index]);
        break;
      }

      case 'Escape': {
        if (!isDropdownOpened) return;

        this.selectController.toggle(false);
      }

      default:
        break;
    }
  }
}
